import { createAsyncThunk, createSlice, isAnyOf } from "@reduxjs/toolkit";
import API from "../../../utils/helpers/APIHelper";
import { RootState } from "../../store";
import ErrorHandler from "../../../utils/helpers/ErrorHandler";
import { ENDPOINTS } from "../../../API/apiConstants";
import {
  BookingRequestPayload,
  ConfirmBookingResponse,
  PaymentIntent,
  PaymentMethodsResponse,
  PaymentSucessRequestPayload,
  ReserveBookingResponse,
} from "./PaymentReducerTypes";
import successHandler from "utils/helpers/SuccessHandler";

type PaymentInitialState = {
  loading: boolean;
  paymentIntent: null;
  reserveBooking: ReserveBookingResponse;
};

const initialState: PaymentInitialState = {
  loading: false,
  paymentIntent: null,
  reserveBooking: {
    booking: null,
    booking_query: "",
    success: false,
  },
};

export const fetchPaymentIntent = createAsyncThunk<
  any,
  PaymentIntent,
  { state: RootState; rejectValue: any }
>("payment/fetchPaymentIntent", async (payload, { rejectWithValue }) => {
  try {
    const response = await API.post(ENDPOINTS.PAYMET_INTENT, payload);
    return response;
  } catch (error) {
    ErrorHandler(error);
    return rejectWithValue(error);
  }
});

export const reserveBooking = createAsyncThunk<
  ReserveBookingResponse,
  BookingRequestPayload,
  { state: RootState; rejectValue: any }
>("payment/reserveBooking", async (payload, { rejectWithValue }) => {
  try {
    const response = await API.post(ENDPOINTS.BOOKING, payload);
    return response.data;
  } catch (error) {
    ErrorHandler(error);
    return rejectWithValue(error);
  }
});

export const paymentSuccess = createAsyncThunk<
  ConfirmBookingResponse,
  PaymentSucessRequestPayload,
  { state: RootState; rejectValue: any }
>("payment/paymentSuccess", async (payload, { getState, rejectWithValue }) => {
  const booking = getState().payment.reserveBooking.booking;
  const bookingId = booking && booking.id ? booking.id : 0;
  try {
    const response = await API.put(
      ENDPOINTS.PAYMENT_SUCCESS(bookingId),
      payload
    );
    if (response.data.status === "confirmed") {
      successHandler("Booking Confirm");
    }
    return response.data;
  } catch (error) {
    ErrorHandler(error);
    return rejectWithValue(error);
  }
});

export const getPaymentMethods = createAsyncThunk<
  PaymentMethodsResponse,
  void,
  { state: RootState; rejectValue: any }
>("payment/getPaymentMethods", async (_, { rejectWithValue }) => {
  try {
    const response = await API.get(ENDPOINTS.PAYMENT_METHODS);
    return response;
  } catch (error) {
    ErrorHandler(error);
    return rejectWithValue(error);
  }
});

export const updateBookingsDetails = createAsyncThunk<
  any,
  { id?: string | number; status: string },
  { state: RootState; rejectValue: any }
>("properties/bookings", async ({ id, status }, { rejectWithValue }) => {
  try {
    const response = await API.put(`${ENDPOINTS.BOOKING_DETAILS}/${id}`, {
      booking: { status },
    });
    return response.data;
  } catch (error) {
    ErrorHandler(error);
    return rejectWithValue(error);
  }
});

export const checkDuplicateBooking = createAsyncThunk<
  any,
  { propertyId: string; roomId?: number; checkIn: string; checkOut: string },
  { rejectValue: any }
>("payment/checkDuplicateBooking", async (payload, { rejectWithValue }) => {
  try {
    const response = await API.get(
      ENDPOINTS.CHECK_DUPLICATE_BOOKING(
        payload.propertyId,
        payload.roomId,
        payload.checkIn,
        payload.checkOut
      )
    );
    return response;
  } catch (error) {
    return rejectWithValue(error);
  }
});

const paymentSlice = createSlice({
  name: "payment",
  initialState,
  reducers: {
    setReserverBooking: (state, action) => {
      state.reserveBooking = {
        ...state.reserveBooking,
        booking: action.payload,
      };
    },
  },
  extraReducers: (builder) => {
    // ── addCase MUST come before addMatcher (Redux Toolkit requirement) ───────

    // ── paymentSuccess fulfilled ─────────────────────────────────────────────
    // Previously missing — this is why room name and updated invoice data
    // were never available on the success screen.
    // The API returns { data: { booking, booking_query }, success: true }
    // so we pull from action.payload.data if present, else treat payload directly.
    builder.addCase(paymentSuccess.fulfilled, (state, action) => {
      state.loading = false;
      const payload = action.payload as any;
      const updated = payload?.data ?? payload;
      if (updated?.booking) {
        state.reserveBooking = {
          booking: updated.booking,
          booking_query: updated.booking_query ?? state.reserveBooking.booking_query,
          success: true,
        };
      }
    });

    // ── addMatcher calls after all addCase calls ──────────────────────────────

    // ── reserveBooking + updateBookingsDetails fulfilled ─────────────────────
    builder.addMatcher(
      isAnyOf(reserveBooking.fulfilled, updateBookingsDetails.fulfilled),
      (state, action) => {
        state.loading = false;
        state.reserveBooking = action.payload;
      }
    );

    // ── loading states ───────────────────────────────────────────────────────
    builder.addMatcher(
      (action) => [
        reserveBooking.pending.type,
        paymentSuccess.pending.type,
      ].includes(action.type),
      (state) => {
        state.loading = true;
      }
    );
    builder.addMatcher(
      (action) => [
        reserveBooking.rejected.type,
        paymentSuccess.rejected.type,
      ].includes(action.type),
      (state) => {
        state.loading = false;
      }
    );
  },
});

export const { setReserverBooking } = paymentSlice.actions;

export default paymentSlice.reducer;